import { useCallback, useEffect, useRef, useState } from "react";
import { TranslationService } from "../../../services/TranslationService";
import { formatDateTimeDigits } from "../../../utils/FormatUtils";
import { restTimezone } from "../../../utils/ParseUtils";
import FileService from "../../../services/FileService";
import { ImageViewer } from "./ImageViewer";
import "./styles.css";
import Loading from "../../../pages/shared/components/Loading";
import { WhatsappMessageType } from "../typings";
import AuthenticationService from "../../../services/AuthenticationService";
import TooltipComponent from "../../../pages/shared/TooltipComponent";

type WhatsappChatType = {
  noMoreResults: boolean;
  isFetching: boolean;
  matrizResults: any[][];
  footer?: any;
  changeSMSSender?: boolean;
};

export const WhatsappChat = ({
  noMoreResults,
  isFetching = false,
  matrizResults,
  footer,
  changeSMSSender = false
}: WhatsappChatType) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [firtsLoadList, setFirtsLoadList] = useState(true);
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [imageViewerUrl, setImageViewerUrl] = useState("");
  const previousMatrizLength = useRef(0);
  const matriz = matrizResults.reduce(
    (acumulador, arrayActual) => acumulador.concat(arrayActual),
    []
  );

  const handleOpenImageViewer = (imageUrl: string) => {
    setImageViewerUrl(imageUrl);
    setImageViewerOpen(true);
  };

  const handleCloseImageViewer = () => {
    setImageViewerOpen(false);
    setImageViewerUrl("");
  };

  const scrollToBottom = () => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    // Solo hacer scroll si:
    // 1. Es la primera carga
    // 2. O si hay nuevos mensajes (la longitud de matriz cambió)
    // No hacer scroll si el visor de imágenes está abierto
    const hasNewMessages = matriz.length !== previousMatrizLength.current;

    if (!imageViewerOpen && !isFetching) {
      if (firtsLoadList || hasNewMessages) {
        scrollToBottom();
        setFirtsLoadList(false);
      }
    }

    previousMatrizLength.current = matriz.length;
  }, [firtsLoadList, isFetching, matriz, imageViewerOpen]);

  return (
    <>
      <div className="chat-container">
        <div className="conversation-container" ref={containerRef}>
          {isFetching ? (
            <i className="fas fa-spinner-third fa-spin third ms-2 text-center w-100 my-4"></i>
          ) : null}
          {changeSMSSender ? <Loading /> : null}

          {matriz.length > 0
            ? matriz
                .sort(
                  (a, b) =>
                    new Date(a.date).getTime() - new Date(b.date).getTime()
                )
                .map((msg) => (
                  <Message
                    key={msg.msgid ?? msg.ID}
                    message={msg}
                    onImageClick={handleOpenImageViewer}
                  />
                ))
            : firtsLoadList &&
              !isFetching && (
                <h5 className="m-2 text-center">
                  {TranslationService.translate.NoResults}
                </h5>
              )}
        </div>
        {!changeSMSSender ? footer : null}
      </div>

      <ImageViewer
        isOpen={imageViewerOpen}
        imageUrl={imageViewerUrl}
        onClose={handleCloseImageViewer}
      />
    </>
  );
};

const Message = ({
  message,
  onImageClick
}: {
  message: WhatsappMessageType;
  onImageClick: (imageUrl: string) => void;
}) => {
  return (
    <div className={`message ${!message.Inbound ? "sent" : "received"}`}>
      <MessageContent message={message} onImageClick={onImageClick} />

      <span className="metadata">
        <span className="time">
          {formatDateTimeDigits(restTimezone(new Date(message.Created)))}
        </span>
        <span className="time"></span>
        {message && message.Status && <StatusMessage message={message} />}
      </span>
    </div>
  );
};

const MessageContent = ({
  message,
  onImageClick
}: {
  message: WhatsappMessageType;
  onImageClick: (imageUrl: string) => void;
}) => {
  const regex = /\.(jpg|jpeg|png|gif|bmp|tiff|webp|pdf|mp3|wav|flac|aac|ogg)$/i;
  const fileDetected = message?.files ? message?.files[0] : null;
  const match = fileDetected?.fileName
    ? fileDetected?.fileName?.toString().match(regex)
    : false;
  const [token, setToken] = useState<string>();
  const [imageKey, setImageKey] = useState(0); // Para forzar re-render de la imagen
  const [audioKey, setAudioKey] = useState(0); // Para forzar re-render del audio
  const refreshTokenIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const imageErrorCountRef = useRef(0);
  const audioErrorCountRef = useRef(0);
  const tokenRef = useRef<string | undefined>(token);

  // Mantener el ref actualizado con el token
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  const downloadFile = () =>
    FileService.downloadFileMessage(
      fileDetected.id,
      message.MessageID,
      fileDetected.fileName
    );

  const refreshToken = useCallback(async () => {
    try {
      const newToken = await AuthenticationService.getAuthenticationToken();
      if (newToken && newToken !== tokenRef.current) {
        setToken(newToken);
        // Forzar re-render de la imagen con el nuevo token
        setImageKey((prev) => prev + 1);
        // Forzar re-render del audio con el nuevo token
        setAudioKey((prev) => prev + 1);
      }
    } catch (error) {
      console.error("[MessageContent] Error al refrescar token:", error);
    }
  }, []);

  useEffect(() => {
    // Obtener token inicial
    refreshToken();

    // Refrescar token periódicamente cada 5 minutos para evitar que expire
    refreshTokenIntervalRef.current = setInterval(
      () => {
        refreshToken();
      },
      5 * 60 * 1000
    ); // 5 minutos

    return () => {
      if (refreshTokenIntervalRef.current) {
        clearInterval(refreshTokenIntervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Solo ejecutar una vez al montar

  const handleImageError = useCallback(() => {
    // Si la imagen falla al cargar, intentar refrescar el token
    // Limitar a 3 intentos para evitar loops infinitos
    if (imageErrorCountRef.current < 3) {
      imageErrorCountRef.current += 1;
      refreshToken();
    }
  }, [refreshToken]);

  const handleAudioError = useCallback(() => {
    // Si el audio falla al cargar, intentar refrescar el token
    // Limitar a 3 intentos para evitar loops infinitos
    if (audioErrorCountRef.current < 3) {
      audioErrorCountRef.current += 1;
      refreshToken();
    }
  }, [refreshToken]);

  if (match) {
    switch (match[1]) {
      case "jpg":
      case "jpeg":
      case "png":
      case "gif":
      case "bmp":
      case "tiff":
      case "webp":
        const imageUrl = token
          ? FileService.getSrcUrl(
              message.AttachmentID,
              message.MessageID || message.ID,
              token
            )
          : "";
        const thumbnailUrl = token
          ? FileService.getThumbSrcUrl(
              message.AttachmentID,
              message.WAMessageID,
              token
            )
          : "";

        return (
          <>
            {token ? (
              <img
                key={`img-${imageKey}`}
                src={thumbnailUrl}
                alt={message.Message}
                onClick={() => imageUrl && onImageClick(imageUrl)}
                onError={handleImageError}
                onLoad={() => {
                  // Resetear contador de errores si la imagen carga correctamente
                  imageErrorCountRef.current = 0;
                }}
                style={{ cursor: "pointer" }}
                title="Click para ampliar"
              />
            ) : null}
            <br />
          </>
        );
      case "pdf":
        return (
          <>
            <button
              className="btn btn-link p-0 m-0 ms-2"
              onClick={downloadFile}
            >
              {fileDetected.fileName}
            </button>
            <br />
          </>
        );
      case "ogg":
      case "wav":
      case "flac":
      case "aac":
      case "mp3":
        const audioUrl = token
          ? FileService.getSrcUrl(
              message.AttachmentID,
              message.MessageID || message.ID,
              token
            )
          : "";
        return (
          <>
            {token ? (
              <audio
                key={`audio-${audioKey}`}
                src={audioUrl}
                controls
                onError={handleAudioError}
                onLoadedData={() => {
                  // Resetear contador de errores si el audio carga correctamente
                  audioErrorCountRef.current = 0;
                }}
              />
            ) : null}
            <br />
          </>
        );
    }
  }

  return (
    <>
      {message.Message}
      <br />
    </>
  );
};
export enum WhatsappStatus {
  failed = 1,
  enqueued = 2,
  delivered = 3,
  sent = 4,
  received = 5,
  read = 6
}

export const StatusMessage = ({
  message
}: {
  message: WhatsappMessageType;
}) => {
  if (!message) {
    return null;
  }

  const status = message.Status;

  if (status === WhatsappStatus.failed) {
    let errorCode: number | null = null;
    let errorReason: string | null = null;

    // Intentar parsear StatusValue si existe
    if (message.StatusValue) {
      try {
        const statusValueJson = JSON.parse(message.StatusValue);
        // La estructura es: Payload.payload.payload.code y Payload.payload.payload.reason
        if (statusValueJson?.Payload?.payload?.payload) {
          errorCode = statusValueJson.Payload.payload.payload.code;
          errorReason = statusValueJson.Payload.payload.payload.reason;
        }
      } catch (error) {
        // Si no se puede parsear, mantener el comportamiento por defecto
        console.error("Error parsing StatusValue:", error);
      }
    }

    // Si tenemos code y reason, mostrar el ícono con tooltip
    if (errorCode !== null && errorReason !== null) {
      return (
        <TooltipComponent title={`Error ${errorCode}: ${errorReason}`}>
          <span className="tick">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16px"
              height="16px"
              viewBox="0 0 512 512"
              version="1.1"
            >
              <g
                id="Page-1"
                stroke="none"
                stroke-width="1"
                fill="none"
                fillRule="evenodd"
              >
                <g
                  id="add"
                  fill="red"
                  transform="translate(32.000000, 42.666667)"
                >
                  <path
                    d="M246.312928,5.62892705 C252.927596,9.40873724 258.409564,14.8907053 262.189374,21.5053731 L444.667042,340.84129 C456.358134,361.300701 449.250007,387.363834 428.790595,399.054926 C422.34376,402.738832 415.04715,404.676552 407.622001,404.676552 L42.6666667,404.676552 C19.1025173,404.676552 7.10542736e-15,385.574034 7.10542736e-15,362.009885 C7.10542736e-15,354.584736 1.93772021,347.288125 5.62162594,340.84129 L188.099293,21.5053731 C199.790385,1.04596203 225.853517,-6.06216498 246.312928,5.62892705 Z M225.144334,42.6739678 L42.6666667,362.009885 L407.622001,362.009885 L225.144334,42.6739678 Z M224,272 C239.238095,272 250.666667,283.264 250.666667,298.624 C250.666667,313.984 239.238095,325.248 224,325.248 C208.415584,325.248 197.333333,313.984 197.333333,298.282667 C197.333333,283.264 208.761905,272 224,272 Z M245.333333,106.666667 L245.333333,234.666667 L202.666667,234.666667 L202.666667,106.666667 L245.333333,106.666667 Z"
                    id="Combined-Shape"
                  ></path>
                </g>
              </g>
            </svg>
          </span>
        </TooltipComponent>
      );
    }

    // Si no se puede parsear, mostrar solo el ícono de warning
    return (
      <span className="tick">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16px"
          height="16px"
          viewBox="0 0 512 512"
          version="1.1"
        >
          <g
            id="Page-1"
            stroke="none"
            stroke-width="1"
            fill="none"
            fillRule="evenodd"
          >
            <g id="add" fill="red" transform="translate(32.000000, 42.666667)">
              <path
                d="M246.312928,5.62892705 C252.927596,9.40873724 258.409564,14.8907053 262.189374,21.5053731 L444.667042,340.84129 C456.358134,361.300701 449.250007,387.363834 428.790595,399.054926 C422.34376,402.738832 415.04715,404.676552 407.622001,404.676552 L42.6666667,404.676552 C19.1025173,404.676552 7.10542736e-15,385.574034 7.10542736e-15,362.009885 C7.10542736e-15,354.584736 1.93772021,347.288125 5.62162594,340.84129 L188.099293,21.5053731 C199.790385,1.04596203 225.853517,-6.06216498 246.312928,5.62892705 Z M225.144334,42.6739678 L42.6666667,362.009885 L407.622001,362.009885 L225.144334,42.6739678 Z M224,272 C239.238095,272 250.666667,283.264 250.666667,298.624 C250.666667,313.984 239.238095,325.248 224,325.248 C208.415584,325.248 197.333333,313.984 197.333333,298.282667 C197.333333,283.264 208.761905,272 224,272 Z M245.333333,106.666667 L245.333333,234.666667 L202.666667,234.666667 L202.666667,106.666667 L245.333333,106.666667 Z"
                id="Combined-Shape"
              ></path>
            </g>
          </g>
        </svg>
      </span>
    );
  }

  if (status === WhatsappStatus.enqueued) {
    return (
      <span className="tick">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16px"
          height="16px"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M12 7V12H15M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
            stroke="grey"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </span>
    );
  }

  if (status === WhatsappStatus.read) {
    return (
      <span className="tick">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="15"
          id="msg-dblcheck-ack"
          x="2063"
          y="2076"
        >
          <path
            d="M15.01 3.316l-.478-.372a.365.365 0 0 0-.51.063L8.666 9.88a.32.32 0 0 1-.484.032l-.358-.325a.32.32 0 0 0-.484.032l-.378.48a.418.418 0 0 0 .036.54l1.32 1.267a.32.32 0 0 0 .484-.034l6.272-8.048a.366.366 0 0 0-.064-.512zm-4.1 0l-.478-.372a.365.365 0 0 0-.51.063L4.566 9.88a.32.32 0 0 1-.484.032L1.892 7.77a.366.366 0 0 0-.516.005l-.423.433a.364.364 0 0 0 .006.514l3.255 3.185a.32.32 0 0 0 .484-.033l6.272-8.048a.365.365 0 0 0-.063-.51z"
            fill="#4fc3f7"
          />
        </svg>
      </span>
    );
  }

  if (status === WhatsappStatus.received) {
    return (
      <span className="tick">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="15"
          id="msg-dblcheck-ack"
          x="2063"
          y="2076"
        >
          <path
            d="M15.01 3.316l-.478-.372a.365.365 0 0 0-.51.063L8.666 9.88a.32.32 0 0 1-.484.032l-.358-.325a.32.32 0 0 0-.484.032l-.378.48a.418.418 0 0 0 .036.54l1.32 1.267a.32.32 0 0 0 .484-.034l6.272-8.048a.366.366 0 0 0-.064-.512zm-4.1 0l-.478-.372a.365.365 0 0 0-.51.063L4.566 9.88a.32.32 0 0 1-.484.032L1.892 7.77a.366.366 0 0 0-.516.005l-.423.433a.364.364 0 0 0 .006.514l3.255 3.185a.32.32 0 0 0 .484-.033l6.272-8.048a.365.365 0 0 0-.063-.51z"
            fill="grey"
          />
        </svg>
      </span>
    );
  }

  return (
    <span className="tick">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="15"
        id="msg-dblcheck-ack"
        x="2063"
        y="2076"
      >
        <path
          d="M15.01 3.316l-.478-.372a.365.365 0 0 0 .484-.034l6.272-8.048a.366.366 0 0 0-.064-.512zm-4.1 0l-.478-.372a.365.365 0 0 0-.51.063L4.566 9.88a.32.32 0 0 1-.484.032L1.892 7.77a.366.366 0 0 0-.516.005l-.423.433a.364.364 0 0 0 .006.514l3.255 3.185a.32.32 0 0 0 .484-.033l6.272-8.048a.365.365 0 0 0-.063-.51z"
          fill="grey"
        />
      </svg>
    </span>
  );
};
